/**
 * Remembers which popup announcements a user has closed, on this device.
 *
 * The key is `<_id>:<updatedAt>` rather than a hash of the content: the API
 * already gives both fields (the model has `timestamps: true`), so an edited
 * announcement gets a new key and shows again, while a hash would need a
 * crypto dependency and be unreadable when debugging.
 *
 * Scoped per user so a shared browser doesn't hide one account's
 * announcements for another. Device-local: clearing site data, or signing in
 * elsewhere, shows the popup again.
 */
const STORAGE_PREFIX = 'dismissedAnnouncements'

export function announcementKey(announcement) {
  if (!announcement || !announcement._id) return ''
  return `${announcement._id}:${announcement.updatedAt || ''}`
}

function storageKey(userId) {
  return `${STORAGE_PREFIX}:${userId || 'anonymous'}`
}

function read(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

export function isAnnouncementDismissed(userId, announcement) {
  const key = announcementKey(announcement)
  if (!key) return false
  return read(userId).includes(key)
}

/**
 * `stillLive` is the announcement list currently returned by the API - keys
 * outside it are pruned so the entry can't grow without bound.
 */
export function dismissAnnouncement(userId, announcement, stillLive = []) {
  const key = announcementKey(announcement)
  if (!key) return

  const liveKeys = stillLive.map(announcementKey).filter(Boolean)
  const next = read(userId)
    .filter((k) => liveKeys.includes(k))
    .concat(key)

  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify(Array.from(new Set(next)))
    )
  } catch (e) {
    // A full or disabled localStorage just means the popup shows again.
  }
}
